import React, { useState } from "react";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { ArrowRight, Coin, Heart, Star, ChartLine, Sparkle, ShieldWarning } from "@phosphor-icons/react";
import { toast } from "sonner";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import SavChart from "../components/SavChart";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PaywallGate from "../components/PaywallGate";

const VERDICT_TINT = {
  very_strong: { bg: "bg-emerald-200", text: "text-emerald-950", label: "VERY STRONG" },
  strong: { bg: "bg-emerald-100", text: "text-emerald-900", label: "STRONG" },
  average: { bg: "bg-slate-100", text: "text-slate-800", label: "AVERAGE" },
  weak: { bg: "bg-amber-100", text: "text-amber-900", label: "WEAK" },
  very_weak: { bg: "bg-rose-100", text: "text-rose-900", label: "VERY WEAK" },
};

function bindCellTint(b) {
  if (b >= 35) return "bg-emerald-200 text-emerald-950";
  if (b >= 30) return "bg-emerald-100 text-emerald-900";
  if (b >= 26) return "bg-slate-100 text-slate-800";
  if (b >= 20) return "bg-amber-100 text-amber-900";
  return "bg-rose-100 text-rose-900";
}

function bavCellTint(b) {
  if (b >= 6) return "bg-emerald-200 text-emerald-950";
  if (b >= 4) return "bg-emerald-100 text-emerald-900";
  if (b >= 3) return "bg-slate-100 text-slate-800";
  if (b >= 2) return "bg-amber-100 text-amber-900";
  return "bg-rose-100 text-rose-900";
}

export default function AshtakavargaPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  usePrefillBirthDetails(setForm, setPlaceQuery);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/ashtakavarga/compute", { ...form, place });
      setData(r.data);
      setTimeout(() => document.querySelector("[data-testid='av-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to compute Ashtakavarga");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <ChartLine size={14} weight="duotone" /> Ashtakavarga · bindu strength of every house & sign
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            Where your chart is <span className="text-gold">strong</span> — and how to align
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-3xl">
            Ashtakavarga is Parashara's quantitative-strength system. For each of your 12 houses (and 12 signs), we sum bindus (auspicious points) from <b>all 7 planets</b> using classical contribution tables. High-bindu houses are where life rewards your effort. We translate the numbers into <b>practical wealth-alignment</b> (e.g. 12H strong → work in foreign lands) and <b>relationship-alignment</b> (lucky signs / signs to attract).
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="av-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="av-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="av-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(sug || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="av-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`av-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="av-submit">
            {loading ? "Computing 337 bindus…" : <>Reveal my Ashtakavarga <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-10" data-testid="av-result">
            {/* === SAV CHART (NORTH-INDIAN STYLE) === */}
            <section className="rounded-2xl p-6 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border border-amber-200" data-testid="sav-chart-section">
              <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                <Star size={14} weight="duotone" /> Sarvashtakavarga chart · total bindus per house
              </div>
              <h2 className="font-serif-display text-3xl mt-2 font-light text-[#3F2D1B]">
                Lagna in <span className="text-gold">{data.lagna_rashi}</span> <span className="text-muted text-base font-light">· {data.sav_total} bindus total (avg ~28/house)</span>
              </h2>
              <div className="mt-5 flex flex-col lg:flex-row gap-6 items-start">
                <div className="flex-shrink-0 mx-auto lg:mx-0">
                  <SavChart savByHouse={data.sav_by_house} lagnaRashi={data.lagna_rashi} size={360} />
                </div>
                <div className="flex-1 space-y-3 text-sm">
                  <p className="text-[#3F2D1B] font-light leading-snug">
                    The number inside each house is your SAV strength — the sum of bindus contributed by all 7 planets. Read this chart like an opportunity-map: <b>green houses are tailwind</b>, <b>red houses are headwind</b>.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-200 border border-emerald-300"></span> 35+ very strong</div>
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-100 border border-emerald-200"></span> 30-34 strong</div>
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-slate-100 border border-slate-300"></span> 26-29 average</div>
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-100 border border-amber-300"></span> 20-25 weak</div>
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-rose-100 border border-rose-300"></span> &lt;20 very weak</div>
                  </div>
                  <div className="pt-3 border-t border-amber-200">
                    <p className="text-xs text-muted">
                      <b>Strongest:</b> House {data.summary.strongest_house} · <b>Weakest:</b> House {data.summary.weakest_house}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* === SAV CELLS GRID (compact alternate view) === */}
            <section className="rounded-2xl p-6 bg-white border border-soft" data-testid="sav-table">
              <div className="label-eyebrow text-muted">House-by-house breakdown</div>
              <p className="text-xs text-muted mt-1 font-light">Compact reference view — each cell shows bindus, sign and verdict.</p>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {data.sav_by_house.map((h) => (
                  <div key={h.house} className={`rounded-xl p-3 ${bindCellTint(h.bindus)} border border-soft`} data-testid={`sav-house-${h.house}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="text-[10px] font-bold tracking-wider opacity-70">H{h.house}</span>
                      <span className="text-2xl font-serif-display font-light">{h.bindus}</span>
                    </div>
                    <div className="text-xs font-medium mt-1">{h.sign}</div>
                    <div className="text-[10px] opacity-75 mt-0.5">{VERDICT_TINT[h.verdict]?.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* === WEALTH INSIGHTS === */}
            <PaywallGate
              tool="ashtakavarga"
              toolLabel="Ashtakavarga · Bindu Strength Map"
              birthDetails={{ date: form.date, time: form.time, place: form.place || placeQuery, timezone: form.timezone }}
              report={data}
            >
              <div className="space-y-10">
              <section className="space-y-5">
              <div className="label-eyebrow text-emerald-900 flex items-center gap-2">
                <Coin size={14} weight="duotone" /> Wealth alignment · work WITH your strongest houses
              </div>
              <p className="text-sm text-muted font-light max-w-3xl">
                Highest-bindu houses are where your chart's gravity pulls you toward success. Stop fighting your chart — pour your hours into the domains below.
              </p>
              {data.wealth_insights.map((w, i) => (
                <article key={w.house} className="rounded-2xl overflow-hidden border border-emerald-200" data-testid={`wealth-${w.house}`}>
                  <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-300 text-emerald-950">#{i + 1} STRONGEST</span>
                      <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-white/80 text-[#3F2D1B] border border-soft">{w.bindus} bindus · {VERDICT_TINT[w.verdict]?.label}</span>
                      <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">House {w.house} · {w.sign}</h3>
                    </div>
                    <p className="text-sm font-medium text-emerald-900 mt-3">{w.title}</p>
                    <p className="text-sm mt-2 font-light leading-snug text-[#3F2D1B]">{w.alignment}</p>
                  </div>
                </article>
              ))}
            </section>

            {/* === RELATIONSHIP INSIGHTS === */}
            <section className="space-y-5" data-testid="rel-section">
              <div className="label-eyebrow text-rose-900 flex items-center gap-2">
                <Heart size={14} weight="duotone" /> Relationship alignment · your lucky signs
              </div>
              <div className="rounded-2xl p-6 bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <div className="label-eyebrow text-emerald-900 flex items-center gap-2"><Sparkle size={12} weight="duotone" /> Attract these signs</div>
                    <div className="mt-3 space-y-2">
                      {data.relationship_insights.lucky_signs.map((s) => (
                        <div key={s.sign} className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-200">
                          <span className="font-medium text-[#3F2D1B]">{s.sign}</span>
                          <span className="text-sm text-emerald-900 font-bold">{s.bindus} bindus</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[#3F2D1B] mt-3 font-light leading-snug">{data.relationship_insights.lucky_partner_note}</p>
                  </div>
                  <div>
                    <div className="label-eyebrow text-rose-900 flex items-center gap-2"><ShieldWarning size={12} weight="duotone" /> Uphill signs</div>
                    <div className="mt-3 space-y-2">
                      {data.relationship_insights.weak_signs.map((s) => (
                        <div key={s.sign} className="flex items-center justify-between p-3 bg-white rounded-lg border border-rose-200">
                          <span className="font-medium text-[#3F2D1B]">{s.sign}</span>
                          <span className="text-sm text-rose-900 font-bold">{s.bindus} bindus</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[#3F2D1B] mt-3 font-light leading-snug">{data.relationship_insights.avoid_partner_note}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl p-6 bg-white border border-soft" data-testid="seventh-house">
                <div className="label-eyebrow text-rose-900">7th house — your partnership axis</div>
                <h3 className="font-serif-display text-2xl mt-2 font-light text-[#3F2D1B]">
                  {data.relationship_insights.seventh_house.sign} <span className="text-muted text-base font-light">· {data.relationship_insights.seventh_house.bindus} bindus · {VERDICT_TINT[data.relationship_insights.seventh_house.verdict]?.label}</span>
                </h3>
                <p className="text-sm text-[#3F2D1B] mt-3 font-light leading-snug">{data.relationship_insights.seventh_house.blurb}</p>
                {data.relationship_insights.seventh_house.verdict === "very_strong" && (
                  <p className="text-xs text-emerald-900 mt-3 italic">Your 7H is exceptionally strong — partnerships and marriage will be a major source of strength and growth in your life.</p>
                )}
                {(data.relationship_insights.seventh_house.verdict === "weak" || data.relationship_insights.seventh_house.verdict === "very_weak") && (
                  <p className="text-xs text-rose-900 mt-3 italic">Your 7H runs weak in bindus — relationship growth needs deliberate work; focus on the lucky signs above when choosing partners and business associates.</p>
                )}
              </div>
            </section>

            {/* === PER-PLANET BAV TABLE === */}
            <section className="rounded-2xl p-6 bg-white border border-soft" data-testid="bav-table">
              <div className="label-eyebrow text-muted flex items-center gap-2">
                <ChartLine size={14} weight="duotone" /> Bhinnashtakavarga · bindus per planet × house
              </div>
              <h2 className="font-serif-display text-2xl mt-2 font-light text-[#3F2D1B]">
                Individual planet strengths
              </h2>
              <p className="text-xs text-muted mt-2 font-light">Each row shows one planet's bindus across your 12 houses. When that planet transits a high-bindu house for YOU, results manifest strongly.</p>
              <div className="mt-5 overflow-x-auto">
                <table className="w-full text-xs min-w-[700px]">
                  <thead>
                    <tr className="border-b border-soft">
                      <th className="text-left py-2 px-2 font-medium text-muted">Planet · Total</th>
                      {Array.from({ length: 12 }, (_, i) => (
                        <th key={i} className="text-center py-2 px-1 font-medium text-muted">H{i + 1}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.bav_totals.map((bt) => (
                      <tr key={bt.planet} className="border-b border-soft/50">
                        <td className="py-2 px-2 font-medium text-[#3F2D1B]">{bt.planet} · <span className="text-muted">{bt.total}</span></td>
                        {data.bav_by_house[bt.planet].map((cell) => (
                          <td key={cell.house} className={`text-center py-1 px-1`}>
                            <span className={`inline-block min-w-[28px] py-1 rounded ${bavCellTint(cell.bindus)} font-bold`}>{cell.bindus}</span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* === PER-PLANET INSIGHTS === */}
            <section className="space-y-3" data-testid="planet-insights">
              <div className="label-eyebrow text-muted">Per-planet transit notes</div>
              {data.bav_insights.map((ins) => (
                <div key={ins.planet} className="rounded-xl p-4 bg-[#FAF4E8] border border-soft">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium text-[#3F2D1B]">{ins.planet}</span>
                    <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-900">peak {ins.max_bindus}</span>
                    <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-rose-200 text-rose-900">low {ins.min_bindus}</span>
                  </div>
                  <p className="text-xs text-[#3F2D1B] mt-2 font-light leading-snug">{ins.transit_note}</p>
                </div>
              ))}
            </section>
              </div>
            </PaywallGate>

            <ToolAstrologerCta
              requiredTechnique="Vedic"
              pitch="Get a live Ashtakavarga walkthrough — which transits to act on, how to time investments, foreign moves and key relationships — with a Vedic specialist"
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
